import { createFileRoute } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { Compass, Lock, Sparkles } from "lucide-react";
import { ExerciseBlock } from "@/components/process/ExerciseBlock";
import { PauseGround } from "@/components/process/PauseGround";
import { useProcessWork } from "@/components/process/useProcessWork";
import { PATTERN_EXERCISES } from "@/lib/know-your-patterns";
import { accentAt, accentVars, ACCENT_CHIP_STYLE, sectionTintStyle } from "@/lib/home-accents";

export const Route = createFileRoute("/know-your-patterns")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Know Your Patterns — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Understand your automatic responses without judgement: fight, flight, freeze and fawn, trigger mapping, avoidance, people-pleasing, shutdown, hypervigilance and body signals.",
      },
      { property: "og:title", content: "Know Your Patterns — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Gentle, private pattern-mapping exercises — meet your responses with curiosity instead of self-blame.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/know-your-patterns" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/know-your-patterns" },
    ],
  }),
  component: withMemberGate(KnowYourPatternsPage, "Know Your Patterns"),
});

function KnowYourPatternsPage() {
  const work = useProcessWork("know_your_patterns");

  return (
    <div className="min-h-screen pb-24" style={sectionTintStyle("lavender")}>
      <div className="mx-auto max-w-3xl px-4 pt-6 sm:px-6">

        <header className="mt-4">
          <p className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden /> Process · Know Your
            Patterns
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-foreground sm:text-5xl">
            Patterns Aren't Flaws
          </h1>
          <p className="mt-3 text-[1.02rem] leading-relaxed text-muted-foreground">
            The ways you respond under stress were learned for good reasons. This
            space is for noticing them with curiosity — not for fixing yourself.
            Take one exercise at a time, and leave anything that isn't for today.
          </p>

          <div className="mt-5 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Private to you
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              <Compass className="h-3.5 w-3.5" aria-hidden /> Come back any time
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              {work.savedCount} of {PATTERN_EXERCISES.length} started
            </span>
          </div>

          <p className="mt-5 text-xs text-muted-foreground">
            Everything you write is saved automatically to your private account
            when you press "Save for later".
          </p>
        </header>

        <p className="mt-5 rounded-2xl border border-border/70 bg-card/60 px-4 py-3 text-sm leading-relaxed text-muted-foreground">
          If you haven't yet, it can help to spend time with{" "}
          <a href="/safety-stabilisation" className="font-medium text-foreground underline underline-offset-4">
            Safety &amp; Stabilisation
          </a>{" "}
          first, so you have grounding options close by.
        </p>

        <nav aria-label="Exercises" className="mt-6 flex flex-wrap gap-2">
          {PATTERN_EXERCISES.map((exercise, index) => (
            <a
              key={exercise.key}
              href={`#${exercise.key}`}
              style={{ ...accentVars(accentAt(index)), ...ACCENT_CHIP_STYLE }}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition hover:opacity-85"
            >
              <span aria-hidden>{exercise.emoji}</span> {exercise.title}
            </a>
          ))}
        </nav>

        <PauseGround className="mt-7" />

        <div className="mt-7 space-y-6">
          {PATTERN_EXERCISES.map((exercise, index) => (
            <ExerciseBlock
              key={exercise.key}
              exercise={exercise}
              entry={work.forKey(exercise.key)}
              onSave={work.saveEntry}
              isSaving={work.isSaving}
              accent={accentAt(index)}
            />
          ))}
        </div>

        <PauseGround className="mt-8" />
      </div>
    </div>
  );
}
